import type { CalendarBooking, LockedPeriod } from '../api/schemas';
import type { IsoDate } from '../calendar/monthGrid';
import { TYPE_FULL, validateDates } from './bookingForm';

interface DateRange {
  from: IsoDate;
  to: IsoDate;
}

/**
 * The nights of `next` that `old` does not already hold, in date order: none, one range, or two
 * when `next` extends `old` at both ends. The backend's `addedNights` does the same.
 */
export const addedNights = (old: DateRange, next: DateRange): DateRange[] => {
  if (next.to <= old.from || next.from >= old.to) {
    return [next];
  }
  const ranges: DateRange[] = [];
  if (next.from < old.from) {
    ranges.push({ from: next.from, to: old.from });
  }
  if (next.to > old.to) {
    ranges.push({ from: old.to, to: next.to });
  }
  return ranges;
};

/**
 * A new booking's date rules for moving `stay`, except that the stay never stands in its own way:
 * it is left out of `bookings`, and only the nights it adds are checked against `locked`.
 */
export const validateChange = (
  stay: CalendarBooking,
  next: DateRange,
  bookings: CalendarBooking[],
  locked: LockedPeriod[],
  today: IsoDate
): Record<string, string> => {
  const others = bookings.filter((b) => !(b.id === stay.id && b.type === stay.type));
  const errors = validateDates(next, others, today);
  if (Object.keys(errors).length > 0) {
    return errors;
  }
  const added = addedNights(stay, next);
  if (added.some((a) => locked.some((l) => a.from < l.to && l.from < a.to))) {
    return { to: TYPE_FULL };
  }
  return {};
};

/**
 * The owner's own stay that has not started yet. The server also checks that the VisBook order is
 * the owner's and unbilled, and answers with a message the sheet shows when it is not.
 */
export const canChangeStay = (booking: CalendarBooking, today: IsoDate): boolean =>
  booking.type === 'own' && booking.status === 'booked' && booking.from > today;
